import React, { useState, useEffect } from "react";
import { MdDelete, MdEdit, MdAdd, MdFileDownload } from "react-icons/md";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { Spinner as SpinnerBoot } from "react-bootstrap";
import { formatDate, formatTime, today } from "../../Helper/Helper";
import { useSelector } from "react-redux";
import { SendPostRequest } from "../../http";
import Spinner from "../Spinner";
import { saveAs } from "file-saver";
import Select from "react-select";
import jsPDF from "jspdf";
import "jspdf-autotable";

// Custom CSS for styling
const customStyles = `
  .action-buttons .btn {
    padding: 0.25rem 0.5rem;
  }
  .action-buttons {
    display: flex;
    justify-content: center;
  }
  .blood-pressure-container table {
    font-size: 14px;
  }
  .blood-pressure-container table th,
  .blood-pressure-container table td {
    white-space: nowrap;
    padding: 0.5rem;
  }
  .btn-add-record {
    background-color: #dc3545 !important;
    border-color: #dc3545 !important;
  }
  .btn-add-record:hover {
    background-color: #bb2d3b !important;
    border-color: #b02a37 !important;
  }
`;

function BloodPressureList() {
  const patientData = useSelector((store) => store.patient.patientLogin);

  const [isLoading, setIsLoading] = useState(false);
  const [LoadingEdit, setLoadingEdit] = useState(false);
  const [LoadingDelete, setLoadingDelete] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [getData, setGetData] = useState([]);
  const [editData, setEditData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const sortOptions = [
    { value: 'record_date-desc', label: 'Date (Newest First)' },
    { value: 'record_date-asc', label: 'Date (Oldest First)' },
    { value: 'systolic-desc', label: 'Systolic (High to Low)' },
    { value: 'systolic-asc', label: 'Systolic (Low to High)' },
    { value: 'diastolic-desc', label: 'Diastolic (High to Low)' },
    { value: 'diastolic-asc', label: 'Diastolic (Low to High)' },
  ];

  const [sortOption, setSortOption] = useState(sortOptions[0]);

  useEffect(() => {
    // Add custom styles
    const style = document.createElement('style');
    style.textContent = customStyles;
    document.head.appendChild(style);

    window.scrollTo(0, 0);
    fetchData();

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fetchData = async (filter = {}) => {
    setIsLoading(true);
    try {
      const response = await SendPostRequest("healthcare/blood_pressure/grid", {
        logged_id: patientData?.logged_id,
        filter: filter
      });
      if (response?.status === "success") {
        setGetData(response?.data || []);
      } else {
        toast.error(response?.message);
        setGetData([]);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to fetch data");
      setGetData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalOpen = (item = null) => {
    if (item) {
      setIsAddMode(false);
      setEditData(item);
    } else {
      setIsAddMode(true);
      setEditData({
        systolic: "",
        diastolic: "",
        pulse: "",
        record_date: "",
        record_time: "",
        // record_date: today,
        // record_time: new Date().toTimeString().slice(0, 5),
        notes: "",
      });
    }
    setValidationErrors({});
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditData({});
    setValidationErrors({});
  };

  // Add this function to prevent minus and decimal input
  const handleKeyDown = (e) => {
    // Prevent minus sign and decimal point input
    if (e.key === '-' || e.key === '.') {
      e.preventDefault();
    }
  };

  // Update the handleOnChange function
  const handleOnChange = (e) => {
    const { value, name } = e.target;

    // Validate inputs
    let errors = { ...validationErrors };

    // Check for decimal values
    if ((name === 'systolic' || name === 'diastolic' || name === 'pulse') && value.includes('.')) {
      errors[name] = 'Decimal values are not allowed. Use whole numbers only.';
      setValidationErrors(errors);

      setEditData((prv) => ({ ...prv, [name]: Math.floor(value) }));
      return;
    }

    // Check for non-integer values
    if ((name === 'systolic' || name === 'diastolic' || name === 'pulse') && !/^\d*$/.test(value)) {
      errors[name] = 'Please enter a valid whole number';
      setValidationErrors(errors);
      return;
    }

    // Validate ranges
    if (name === 'systolic') {
      const systolicValue = parseInt(value) || 0;
      if (systolicValue < 40 || systolicValue > 370) {
        errors.systolic = 'Systolic must be between 40-370 mmHg';
      } else {
        delete errors.systolic;

        // Check if diastolic is already set and validate
        const diastolicValue = parseInt(editData.diastolic) || 0;
        if (diastolicValue > 0 && systolicValue <= diastolicValue) {
          errors.systolic = 'Systolic value must be greater than Diastolic Value';
        } else if (errors.diastolic && errors.diastolic.includes('Diastolic value must be smaller than Systolic Value')) {
          delete errors.diastolic;
        }
      }
    }

    if (name === 'diastolic') {
      const diastolicValue = parseInt(value) || 0;
      if (diastolicValue < 20 || diastolicValue > 260) {
        errors.diastolic = 'Diastolic must be between 20-260 mmHg';
      } else {
        delete errors.diastolic;

        // Check if systolic is already set and validate
        const systolicValue = parseInt(editData.systolic) || 0;
        if (systolicValue > 0 && diastolicValue >= systolicValue) {
          errors.diastolic = 'Diastolic value must be smaller than Systolic Value';
        } else if (errors.systolic && errors.systolic.includes('Systolic value cannot be smaller')) {
          delete errors.systolic;
        }
      }
    }

    if (name === 'pulse') {
      const pulseValue = parseInt(value) || 0;
      if (pulseValue < 7 || pulseValue > 480) {
        errors.pulse = 'Pulse must be between 7-480 bpm';
      } else {
        delete errors.pulse;
      }
    }

    setValidationErrors(errors);
    setEditData((prv) => ({ ...prv, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};

    const systolicValue = parseInt(editData.systolic) || 0;
    const diastolicValue = parseInt(editData.diastolic) || 0;
    const pulseValue = parseInt(editData.pulse) || 0;

    if (!editData.systolic || systolicValue < 40 || systolicValue > 370) {
      errors.systolic = 'Systolic must be between 40-370 mmHg';
    }

    if (!editData.diastolic || diastolicValue < 20 || diastolicValue > 260) {
      errors.diastolic = 'Diastolic must be between 20-260 mmHg';
    }

    if (systolicValue > 0 && diastolicValue > 0 && systolicValue <= diastolicValue) {
      errors.systolic = 'Systolic value must be greater than Diastolic Value';
    }

    if (!editData.pulse || pulseValue < 7 || pulseValue > 480) {
      errors.pulse = 'Pulse must be between 7-480 bpm';
    }

    if (!editData.record_date) {
      errors.record_date = 'Date is required';
    }

    if (!editData.record_time) {
      errors.record_time = 'Time is required';
    }
    if (editData.record_date && editData.record_time) {
      const selectedDateTime = new Date(`${editData.record_date}T${editData.record_time}`);
      const now = new Date();
      if (selectedDateTime > now) {
        errors.record_time = 'Future times are not allowed';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEdit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const data = {
      logged_id: patientData?.logged_id,
      ...(isAddMode
        ? {
          form_data: editData,
        }
        : {
          record_id: editData?.id,
          form_data: editData,
        }),
    };

    setLoadingEdit(true);
    try {
      const response = await SendPostRequest(
        isAddMode
          ? "healthcare/blood_pressure/add"
          : "healthcare/blood_pressure/edit",
        data
      );
      if (response?.status === "success") {
        toast.success(
          isAddMode
            ? "Record has been added successfully"
            : "Record has been updated successfully"
        );
        handleModalClose();
        fetchData(filterData);
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to save record");
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleDelete = async () => {
    setLoadingDelete(true);
    try {
      const response = await SendPostRequest(
        "healthcare/blood_pressure/delete",
        { logged_id: patientData?.logged_id, record_id: deleteId }
      );
      if (response?.status === "success") {
        toast.success(response?.message);
        fetchData(filterData);
        setShowDeleteConfirm(false);
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to delete record");
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleFilterChange = (e) => {
    const { value, name } = e.target;
    setFilterData((prv) => ({ ...prv, [name]: value }));
  };

  const handleFilter = (e) => {
    e.preventDefault();

    // Validate date filter if dates are provided
    if (filterData.date_from && filterData.date_to) {
      if (new Date(filterData.date_from) > new Date(filterData.date_to)) {
        toast.error("From date cannot be after to date");
        return;
      }
    }

    fetchData(filterData);
  };

  const clearFilter = () => {
    setFilterData({});
    fetchData({});
  };

  const sortedData = [...getData].sort((a, b) => {
    const [field, direction] = sortOption.value.split('-');

    if (field === 'record_date') {
      const dateA = new Date(a.record_date);
      const dateB = new Date(b.record_date);
      return direction === 'desc' ? dateB - dateA : dateA - dateB;
    } else {
      const valueA = a[field];
      const valueB = b[field];
      return direction === 'desc' ? valueB - valueA : valueA - valueB;
    }
  });

  const exportCSV = () => {
    if (sortedData.length === 0) {
      toast.info("No data available to export");
      return;
    }

    const header = [
      "SR.No",
      "Record Date",
      "Record Time",
      "Systolic (mmHg)",
      "Diastolic (mmHg)",
      "Pulse (bpm)",
      "Note",
      "Status"
    ];

    const rows = sortedData.map((d, index) => [
      index + 1,
      formatDate(d.record_date),
      formatTime(d.record_time),
      d.systolic,
      d.diastolic,
      d.pulse,
      d.notes || "",
      d.status || ""
    ]);

    // Summary values
    const systolicValues = sortedData.map(item => parseInt(item.systolic));
    const diastolicValues = sortedData.map(item => parseInt(item.diastolic));
    const pulseValues = sortedData.map(item => parseInt(item.pulse));

    const systolicAvg = systolicValues.reduce((a, b) => a + b, 0) / systolicValues.length;
    const diastolicAvg = diastolicValues.reduce((a, b) => a + b, 0) / diastolicValues.length;
    const pulseAvg = pulseValues.reduce((a, b) => a + b, 0) / pulseValues.length;

    const systolicMax = Math.max(...systolicValues);
    const diastolicMax = Math.max(...diastolicValues);
    const pulseMax = Math.max(...pulseValues);

    const systolicMin = Math.min(...systolicValues);
    const diastolicMin = Math.min(...diastolicValues);
    const pulseMin = Math.min(...pulseValues);

    // Create CSV rows
    let csvArray = [];

    // 1. Add header + data rows
    csvArray.push(header);
    csvArray.push(...rows);

    // 2. Empty row before summary
    csvArray.push([]);

    // 3. Summary section (aligned properly, only 2 columns used)
    csvArray.push(["Summary Statistics"]);
    csvArray.push(["Systolic - Average", systolicAvg.toFixed(1)]);
    csvArray.push(["Systolic - Maximum", systolicMax]);
    csvArray.push(["Systolic - Minimum", systolicMin]);
    csvArray.push(["Diastolic - Average", diastolicAvg.toFixed(1)]);
    csvArray.push(["Diastolic - Maximum", diastolicMax]);
    csvArray.push(["Diastolic - Minimum", diastolicMin]);
    csvArray.push(["Pulse - Average", pulseAvg.toFixed(1)]);
    csvArray.push(["Pulse - Maximum", pulseMax]);
    csvArray.push(["Pulse - Minimum", pulseMin]);

    // 4. Add report generated date
    csvArray.push([]);
    csvArray.push(["Report Generated On", formatDate(new Date())]);

    // Convert to CSV string
    const csvContent = csvArray
      .map((e) => e.map(field => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `blood_pressure_records_${formatDate(new Date())}.csv`);
  };

  const exportToPDF = () => {
    if (getData.length === 0) {
      toast.info("No data available to export");
      return;
    }

    import("jspdf").then((jsPDFModule) => {
      import("jspdf-autotable").then((autoTableModule) => {
        const { jsPDF } = jsPDFModule;
        const { default: autoTable } = autoTableModule;

        const doc = new jsPDF();

        // Title
        doc.setFontSize(18);
        doc.text("Blood Pressure Records", 14, 22);
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Generated on: ${formatDate(today)}`, 14, 30);

        // Date range if filtered
        if (filterData?.date_from && filterData?.date_to) {
          doc.text(
            `Date Range: ${formatDate(filterData.date_from)} to ${formatDate(filterData.date_to)}`,
            14,
            40
          );
        }

        // Prepare table body with SR.No
        const tableBody = sortedData.map((item, index) => [
          index + 1,
          formatDate(item.record_date),
          formatTime(item.record_time),
          item.systolic,
          item.diastolic,
          item.pulse,
          item.notes || "-",
          item.status || "-",
        ]);

        // Generate table
        autoTable(doc, {
          startY: filterData?.date_from && filterData?.date_to ? 50 : 40,
          head: [
            [
              "SR.No",
              "Date",
              "Time",
              "Systolic (mmHg)",
              "Diastolic (mmHg)",
              "Pulse (bpm)",
              "Notes",
              "Status",
            ],
          ],
          body: tableBody,
          theme: "grid",
          headStyles: {
            fillColor: [66, 139, 202],
            textColor: [255, 255, 255],
            fontSize: 10,
          },
          bodyStyles: {
            fontSize: 9,
          },
          columnStyles: {
            0: { cellWidth: 15 },
            1: { cellWidth: 25 },
            2: { cellWidth: 15 },
            3: { cellWidth: 25 },
            4: { cellWidth: 25 },
            5: { cellWidth: 15 },
            6: { cellWidth: 30 },
            7: { cellWidth: 35 },
          },
          didDrawPage: function (data) {
            // Page number footer
            const pageCount = doc.internal.getNumberOfPages();
            const pageSize = doc.internal.pageSize;
            const pageHeight = pageSize.height
              ? pageSize.height
              : pageSize.getHeight();
            doc.setFontSize(9);
            doc.text(
              `Page ${pageCount}`,
              data.settings.margin.left,
              pageHeight - 5
            );
          },
        });

        // Summary statistics
        const systolicValues = sortedData.map((item) => parseInt(item.systolic));
        const diastolicValues = sortedData.map((item) =>
          parseInt(item.diastolic)
        );
        const pulseValues = sortedData.map((item) => parseInt(item.pulse));

        const systolicAvg =
          systolicValues.reduce((a, b) => a + b, 0) / systolicValues.length;
        const diastolicAvg =
          diastolicValues.reduce((a, b) => a + b, 0) / diastolicValues.length;
        const pulseAvg =
          pulseValues.reduce((a, b) => a + b, 0) / pulseValues.length;

        const systolicMax = Math.max(...systolicValues);
        const diastolicMax = Math.max(...diastolicValues);
        const pulseMax = Math.max(...pulseValues);

        const systolicMin = Math.min(...systolicValues);
        const diastolicMin = Math.min(...diastolicValues);
        const pulseMin = Math.min(...pulseValues);

        let finalY = doc.lastAutoTable.finalY + 15;

        // If summary doesn't fit on current page → add new page
        if (finalY > doc.internal.pageSize.height - 40) {
          doc.addPage();
          finalY = 20;
        }

        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text("Summary Statistics", 14, finalY);

        doc.setFontSize(10);
        doc.text(
          `Systolic - Avg: ${systolicAvg.toFixed(
            1
          )}, Max: ${systolicMax}, Min: ${systolicMin}`,
          14,
          finalY + 10
        );
        doc.text(
          `Diastolic - Avg: ${diastolicAvg.toFixed(
            1
          )}, Max: ${diastolicMax}, Min: ${diastolicMin}`,
          14,
          finalY + 17
        );
        doc.text(
          `Pulse - Avg: ${pulseAvg.toFixed(1)}, Max: ${pulseMax}, Min: ${pulseMin}`,
          14,
          finalY + 24
        );

        // Save file
        doc.save(`blood_pressure_records_${today}.pdf`);
      });
    }).catch((error) => {
      console.error("Error loading PDF libraries:", error);
      toast.error("Failed to generate PDF");
    });
  };




  // const exportToPDF = () => {
  //   if (getData.length === 0) {
  //     toast.info("No data available to export");
  //     return;
  //   }

  //   import('jspdf').then(jsPDFModule => {
  //     import('jspdf-autotable').then(autoTableModule => {
  //       const { jsPDF } = jsPDFModule;
  //       const { default: autoTable } = autoTableModule;

  //       const doc = new jsPDF();

  //       // Title
  //       doc.setFontSize(18);
  //       doc.text("Blood Pressure Records", 14, 22);
  //       doc.setFontSize(11);
  //       doc.setTextColor(100);
  //       doc.text(`Generated on: ${formatDate(today)}`, 14, 30);

  //       // Date range if filtered
  //       if (filterData?.date_from && filterData?.date_to) {
  //         doc.text(`Date Range: ${formatDate(filterData.date_from)} to ${formatDate(filterData.date_to)}`, 14, 40);
  //       }

  //       // Prepare table body with SR.No
  //       const tableBody = sortedData.map((item, index) => [
  //         index + 1,
  //         formatDate(item.record_date),
  //         formatTime(item.record_time),
  //         item.systolic,
  //         item.diastolic,
  //         item.pulse,
  //         item.notes || "-",
  //         item.status || "-"
  //       ]);

  //       // Generate table
  //       autoTable(doc, {
  //         startY: filterData?.date_from && filterData?.date_to ? 50 : 40,
  //         head: [["SR.No", "Date", "Time", "Systolic (mmHg)", "Diastolic (mmHg)", "Pulse (bpm)", "Notes", "Status"]],
  //         body: tableBody,
  //         theme: 'grid',
  //         headStyles: {
  //           fillColor: [66, 139, 202],
  //           textColor: [255, 255, 255],
  //           fontSize: 10,
  //         },
  //         bodyStyles: {
  //           fontSize: 9,
  //         },
  //         columnStyles: {
  //           0: { cellWidth: 15 },
  //           1: { cellWidth: 25 },
  //           2: { cellWidth: 20 },
  //           3: { cellWidth: 25 },
  //           4: { cellWidth: 25 },
  //           5: { cellWidth: 20 },
  //           6: { cellWidth: 40 },
  //           7: { cellWidth: 20 }
  //         }
  //       });

  //       // Summary statistics
  //       const systolicValues = sortedData.map(item => parseInt(item.systolic));
  //       const diastolicValues = sortedData.map(item => parseInt(item.diastolic));
  //       const pulseValues = sortedData.map(item => parseInt(item.pulse));

  //       const systolicAvg = systolicValues.reduce((a, b) => a + b, 0) / systolicValues.length;
  //       const diastolicAvg = diastolicValues.reduce((a, b) => a + b, 0) / diastolicValues.length;
  //       const pulseAvg = pulseValues.reduce((a, b) => a + b, 0) / pulseValues.length;

  //       const systolicMax = Math.max(...systolicValues);
  //       const diastolicMax = Math.max(...diastolicValues);
  //       const pulseMax = Math.max(...pulseValues);

  //       const systolicMin = Math.min(...systolicValues);
  //       const diastolicMin = Math.min(...diastolicValues);
  //       const pulseMin = Math.min(...pulseValues);

  //       let finalY = doc.lastAutoTable.finalY + 15;

  //       doc.setFontSize(14);
  //       doc.setTextColor(0, 0, 0);
  //       doc.text("Summary Statistics", 14, finalY);

  //       doc.setFontSize(10);
  //       doc.text(`Systolic - Avg: ${systolicAvg.toFixed(1)}, Max: ${systolicMax}, Min: ${systolicMin}`, 14, finalY + 10);
  //       doc.text(`Diastolic - Avg: ${diastolicAvg.toFixed(1)}, Max: ${diastolicMax}, Min: ${diastolicMin}`, 14, finalY + 17);
  //       doc.text(`Pulse - Avg: ${pulseAvg.toFixed(1)}, Max: ${pulseMax}, Min: ${pulseMin}`, 14, finalY + 24);

  //       // Save file
  //       doc.save(`blood_pressure_records_${today}.pdf`);
  //     });
  //   }).catch(error => {
  //     console.error("Error loading PDF libraries:", error);
  //     toast.error("Failed to generate PDF");
  //   });
  // };

  const getStatusClass = (status) => {
    if (!status) return "secondary";

    if (status.includes("Crisis") || status.includes("Hypertensive")) return "danger";
    if (status.includes("High") || status.includes("Stage 2")) return "danger";
    if (status.includes("Stage 1") || status.includes("Elevated")) return "warning";
    if (status.includes("Normal")) return "success";
    return "secondary";
  };
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return (
    <>
      <div className="blood-pressure-container Prescripition">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
          <h3 className="mb-0">Blood Pressure</h3>
          <div className="d-flex gap-2">
            <Button
              className="btn btn-default d-flex"
              onClick={() => handleModalOpen()}
              style={{ alignItems: "center" }}
            >
              <MdAdd className="me-1" /> Add Record
            </Button>
            <div className="btn-group border-none-csv-pdf">
              <Button
                variant="outline-success"
                onClick={exportCSV}
                className=" btn-default d-flex"
                style={{ alignItems: "center" }}
                disabled={getData.length === 0}
              >
                <MdFileDownload className="me-1" /> CSV
              </Button>
              <Button
                variant="outline-danger"
                onClick={exportToPDF}
                className=" btn-default d-flex"
                style={{ alignItems: "center" }}
                disabled={getData.length === 0}
              >
                <MdFileDownload className="me-1" /> PDF
              </Button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="d-flex align-items-center justify-content-center p-5">
            <Spinner />
          </div>
        ) : (
          <div className="table-responsive">
            {/* Filter + sort row (aligned like Weight Management list) */}
            <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
              {/* Filter */}
              <form
                onSubmit={handleFilter}
                className="d-flex flex-wrap align-items-end gap-3"
              >
                <div className="form-group mb-0">
                  <label className="fw-bold mb-0">From:</label>
                  <input
                    type="date"
                    name="date_from"
                    className="form-control"
                    value={filterData?.date_from || ""}
                    onChange={handleFilterChange}
                    max={today}
                    style={{ width: "auto", minWidth: 150 }}
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="fw-bold mb-0">To:</label>
                  <input
                    type="date"
                    name="date_to"
                    className="form-control"
                    value={filterData?.date_to || ""}
                    onChange={handleFilterChange}
                    max={today}
                    style={{ width: "auto", minWidth: 150 }}
                  />
                </div>
                <Button
                  type="submit"
                  variant="btn btn-default"
                  className="btn btn-default"
                >
                  Filter
                </Button>
                <Button
                  type="button"
                  variant="btn btn-default"
                  className="btn btn-default"
                  onClick={clearFilter}
                >
                  Clear
                </Button>
              </form>

              {/* Sorting */}
              <div
                className={`d-flex align-items-end mt-2 ${
                  !isMobile ? "ms-auto" : ""
                }`}
              >
                <Select
                  className="sort-dropdown"
                  value={sortOption}
                  options={sortOptions}
                  onChange={setSortOption}
                  isSearchable={false}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      fontSize: "14px",
                    }),
                  }}
                />
              </div>
            </div>

            {/* Table */}
            <div className="table-responsive">
              <table className="table mt-2">
                <thead className="thead-light">
                  <tr>
                    <th
                      scope="col"
                      style={{ width: "10%", textAlign: "center" }}
                    >
                      SR.No
                    </th>
                    <th scope="col">Record Date</th>
                    <th scope="col">Record Time</th>
                    <th scope="col">Systolic (mmHg)</th>
                    <th scope="col">Diastolic (mmHg)</th>
                    <th scope="col">Pulse (bpm)</th>
                    {/* <th scope="col">Note</th> */}
                    <th scope="col">Status</th>
                    <th scope="col" style={{ width: "12%" }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center py-4">
                        No blood pressure records found
                      </td>
                    </tr>
                  ) : (
                    sortedData.map((item, index) => {
                      const statusColor = item?.color || "#000";

                      return (
                        <tr key={item.id}>
                          <td style={{ textAlign: "center" }}>{index + 1}</td>
                          <td>{formatDate(item.record_date)}</td>
                          <td>{formatTime(item.record_time)}</td>
                          <td>{item.systolic}</td>
                          <td>{item.diastolic}</td>
                          <td>{item.pulse}</td>
                          {/* <td>{item.notes || "-"}</td> */}
                          <td>
                            <span
                              className={`badge`}
                              style={{
                                backgroundColor: statusColor,
                                color: "#fff",
                                textTransform: "capitalize",
                                fontSize: "10px",
                              }}
                            >
                              {item.status || "Unknown"}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn btn-default"
                                onClick={() => handleModalOpen(item)}
                                title="Edit"
                              >
                                <MdEdit />
                              </button>
                              <button
                                className="btn btn-default"
                                onClick={() => {
                                  setDeleteId(item.id);
                                  setShowDeleteConfirm(true);
                                }}
                                disabled={LoadingDelete}
                                title="Delete"
                              >
                                {LoadingDelete && deleteId === item.id ? (
                                  <SpinnerBoot size="sm" />
                                ) : (
                                  <MdDelete />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        show={showModal}
        onHide={handleModalClose}
        centered
        size="lg"
        className="blood-pressure-modal"
      >
        <form onSubmit={handleEdit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {isAddMode
                ? "Add Blood Pressure Record"
                : "Edit Blood Pressure Record"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-lg-6">
                <label className="form-label fw-bold">Diastolic (mmHg)*</label>
                <input
                  required
                  type="number"
                  placeholder="20-260"
                  className={`form-control mb-0 ${
                    validationErrors.diastolic ? "is-invalid" : ""
                  }`}
                  value={editData?.diastolic}
                  name="diastolic"
                  onChange={handleOnChange}
                  onKeyDown={handleKeyDown} // Add this line
                  min="20"
                  max="260"
                />
                {validationErrors.diastolic && (
                  <div
                    className="invalid-feedback"
                    style={{ fontSize: "11px" }}
                  >
                    {validationErrors.diastolic}
                  </div>
                )}
              </div>
              <div className="col-lg-6">
                <label className="form-label fw-bold">Systolic (mmHg)*</label>
                <input
                  required
                  type="number"
                  placeholder="40-370"
                  className={`form-control mb-0 ${
                    validationErrors.systolic ? "is-invalid" : ""
                  }`}
                  value={editData?.systolic}
                  name="systolic"
                  onChange={handleOnChange}
                  onKeyDown={handleKeyDown} // Add this line
                  min="40"
                  max="370"
                />
                {validationErrors.systolic && (
                  <div
                    className="invalid-feedback"
                    style={{ fontSize: "11px" }}
                  >
                    {validationErrors.systolic}
                  </div>
                )}
              </div>
              <div className="col-lg-6">
                <label className="form-label fw-bold">Pulse (bpm)*</label>
                <input
                  required
                  type="number"
                  placeholder="7-480"
                  className={`form-control mb-0 ${
                    validationErrors.pulse ? "is-invalid" : ""
                  }`}
                  value={editData?.pulse}
                  name="pulse"
                  onChange={handleOnChange}
                  onKeyDown={handleKeyDown} // Add this line
                  min="7"
                  max="480"
                />
                {validationErrors.pulse && (
                  <div
                    className="invalid-feedback mt-0"
                    style={{ fontSize: "11px" }}
                  >
                    {validationErrors.pulse}
                  </div>
                )}
              </div>
              <div className="col-lg-6">
                <label className="form-label fw-bold">Date*</label>
                <input
                  style={{ height: "50px" }}
                  type="date"
                  className={`form-control ${
                    validationErrors.record_date ? "is-invalid" : ""
                  }`}
                  value={editData?.record_date}
                  name="record_date"
                  onChange={handleOnChange}
                  max={today}
                  required
                />
                {validationErrors.record_date && (
                  <div className="invalid-feedback">
                    {validationErrors.record_date}
                  </div>
                )}
              </div>
              <div className="col-lg-6">
                <label className="form-label fw-bold">Time*</label>
                <input
                  style={{ height: "50px" }}
                  type="time"
                  className={`form-control mb-0 ${
                    validationErrors.record_time ? "is-invalid" : ""
                  }`}
                  value={editData?.record_time}
                  name="record_time"
                  onChange={handleOnChange}
                  required
                />
                {validationErrors.record_time && (
                  <div className="invalid-feedback mt-0">
                    {validationErrors.record_time}
                  </div>
                )}
              </div>
              <div className="col-lg-12">
                <label className="form-label fw-bold">Note</label>
                <textarea
                  placeholder="Add any notes about this measurement"
                  className="form-control"
                  value={editData?.notes}
                  name="notes"
                  onChange={handleOnChange}
                  rows="3"
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="btn btn-default"
              variant="btn btn-default"
              onClick={handleModalClose}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-default"
              type="submit"
              disabled={LoadingEdit}
            >
              {LoadingEdit ? "Saving..." : "Save Record"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this blood pressure record? This
          action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn btn-default"
            variant="btn btn-default"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-default"
            variant="danger"
            onClick={handleDelete}
            disabled={LoadingDelete}
          >
            {LoadingDelete ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default BloodPressureList;